import React, { useMemo, useCallback } from 'react';
import { testId } from '../../../../utils/testIds';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { lookupByZip } from '../../../../containers/CreationForms/AddEditWorkkard/store/addressApi';
import { useAddressSiblings } from '../utils/addressKeys';
import { getSectionFromKey, getAddressPlaceholder } from '../utils/addressLabels';
import { setTimezoneFromId, clearTimezone } from '../../../../containers/CreationForms/AddEditWorkkard/store/timezoneSlice';
import {
  zipLookupRequest,
  zipLookupSuccess,
  zipLookupFailure,
} from '../../../../containers/CreationForms/AddEditWorkkard/store/addressSlice';

function normalizeZipResponse(response) {
  const suggestions = response?.suggestion || response?.suggestions || null;
  const first = Array.isArray(suggestions) ? suggestions[0] : response;
  if (!first) return null;

  return {
    state: first.state || null,
    stateFullName: first.state_full_name || null,
    county: first.county || '',
    city: first.city || null,
    country: first.country || null,
    zipcode: first.zipcode || null,
    timeZoneId: first.time_zone_id || null,
  };
}

function ZipField({ config, value, onChange, onBlur, disabled, required, error }) {
  const { setValue, watch } = useFormContext();
  const dispatch = useDispatch();

  const section = useMemo(() => getSectionFromKey(config.fieldKey), [config.fieldKey]);
  const { countryKey, stateKey, countyKey, cityKey } = useAddressSiblings(config.fieldKey);
  
  const country = watch(countryKey);
  
  const placeholder = useMemo(
    () => config?.fieldSettings?.placeholder || getAddressPlaceholder({ role: 'zip', section }),
    [config, section]
  );

  const safeSet = useCallback((key, val, shouldValidate = true) => {
    if (!key) return;
    setValue(key, val, { shouldDirty: true, shouldValidate });
  }, [setValue]);

  const handleChange = useCallback((e) => {
    const rawValue = e.target.value;
    const newValue = config.maxLength ? rawValue.slice(0, config.maxLength) : rawValue;
    onChange(newValue);

    safeSet(stateKey, '', false);
    safeSet(countyKey, '', false);
    safeSet(cityKey, '', false);

    if (section === 'property') {
      dispatch(clearTimezone());
    }
  }, [onChange, safeSet, stateKey, countyKey, cityKey, section, dispatch, config.maxLength]);

  const handleBlur = useCallback(async () => {
    if (!value || !country) {
      onBlur?.();
      return;
    }

    dispatch(zipLookupRequest());
    try {
      const raw = await lookupByZip({ zip: value, country });
      const result = normalizeZipResponse(raw);

      if (result) {
        if (result.state) safeSet(stateKey, result.state);
        safeSet(countyKey, result.county || '');
        if (result.city) safeSet(cityKey, result.city);

        if (section === 'property' && result.timeZoneId) {
          dispatch(setTimezoneFromId({ timeZoneId: result.timeZoneId }));
        }
        dispatch(zipLookupSuccess(result));
      } else {
        if (section === 'property') dispatch(clearTimezone());
        dispatch(zipLookupSuccess(null));
      }
    } catch (err) {
      console.error('[ZipField] Lookup failed:', err);
      dispatch(zipLookupFailure({ error: err?.message || 'Zip lookup failed' }));
    }

    onBlur?.();
  }, [value, country, safeSet, stateKey, countyKey, cityKey, section, dispatch, onBlur]);

  return (
    <input
      data-test={testId('field', config.fieldKey, 'input')}
      type="text"
      className={`field-engine-input${error ? ' field-engine-input--error' : ''}${disabled ? ' disabled' : ''}`}
      value={value || ''}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      required={required}
      placeholder={placeholder}
      aria-required={required}
      aria-invalid={!!error}
      maxLength={config.maxLength || undefined}
    />
  );
}

export default ZipField;
